<script lang="ts" setup>
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import type { Option } from '@autobid/strapi-integration/typescript/webapi/Dictionary'
import type { Counters } from '@autobid/ui/types/components/CarsList'
import { useNoFilterCounters } from '@autobid/ui/composables/car/data/useNoFilterCounters'
import { VEHICLE_TYPE_KEY } from '@autobid/ui/constants/VEHICLE_TYPE_KEY'
import { injectFilterComposable } from '@autobid/ui/utils/carsList/injectFilterComposable'
import { computed, inject, onServerPrefetch } from 'vue'
import Category from './Category.vue'

interface Props {
  typeLabel: string
  categories: Omit<Option, 'types'>[]
  counters: Counters
}

interface Ids {
  typeId: string
  categoryId: string
}

const props = defineProps<Props>()

const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')
const { data: noFilterMetadata, suspense } = useNoFilterCounters(
  auctionStrapiData?.type === 'carList' ? 'auction' : 'searchList',
)
onServerPrefetch(suspense)

const useFilter = injectFilterComposable()
const {
  filters: { category },
  toggleAll,
  areAllChecked,
} = useFilter()

const ids = computed(() => {
  return props.categories.reduce((acc: Record<string, Ids>, category) => {
    const [typeId, categoryId] = String(category.id).split(';')
    acc[category.id] = { typeId, categoryId }

    return acc
  }, {})
})
const typeId = Object.values(ids.value)[0].typeId
const categoriesToDisplay = computed(() => {
  return props.categories.filter(
    category =>
      noFilterMetadata.value?.counters[VEHICLE_TYPE_KEY]?.[
        ids.value[category.id]?.typeId
      ]?.category[ids.value[category.id].categoryId],
  )
})

const counter = computed(() => props.counters?.[VEHICLE_TYPE_KEY]?.[typeId])

const categoriesPossibleToCheck = computed(() => {
  return categoriesToDisplay.value.filter((category) => {
    const [, categoryId] = String(category.id).split(';')

    return ((counter.value?.category[categoryId] as number) ?? 0) > 0
  })
})

const allChecked = computed(() =>
  areAllChecked(categoriesPossibleToCheck.value, category.value),
)
const allCategoriesCounter = computed(() => counter.value?.total ?? 0)
</script>

<template>
  <div v-if="categoriesToDisplay.length">
    <ElementsCheckbox
      :key="typeLabel + allCategoriesCounter"
      :checked="allChecked"
      :label-suffix="allCategoriesCounter"
      :disabled="allCategoriesCounter === 0"
      :label="typeLabel"
      :small="true"
      @click="() => (category = toggleAll(categoriesPossibleToCheck, category))"
    />
    <common-filters-checkboxes-wrapper class="pl-2">
      <template v-for="{ id, label } in categoriesToDisplay" :key="id">
        <Category
          :id="String(id)"
          :checked="Boolean(category?.includes(String(id)))"
          :counter="Number(counter?.category[ids[id].categoryId] ?? 0)"
          :label="label"
          :type-label="typeLabel"
        />
      </template>
    </common-filters-checkboxes-wrapper>
  </div>
</template>
